import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { ApiService } from '@core/providers/http';
import { catchError, map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { HttpParams } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  isAuthenticated: BehaviorSubject<boolean>;
  private readonly accessTokenKey = 'access_token';
  private readonly refreshTokenKey = 'refresh_token';
  private readonly expiresAtKey = 'expires_at';
  private readonly claimsKey = 'claims';

  constructor(
    private readonly apiService: ApiService,
    private readonly router: Router,
  ) {
    this.isAuthenticated = new BehaviorSubject<boolean>(
      this.checkInitialAuthState(),
    );
  }

  private checkInitialAuthState(): boolean {
    const token = this.getAccessToken();
    const expiresAt = localStorage.getItem(this.expiresAtKey);
    if (!token) return false;
    if (expiresAt) {
      return new Date(expiresAt) > new Date();
    }
    return true;
  }

  register(user: any): Observable<any> {
    return this.apiService.post(`/Auth/Register`, user).pipe(
      map((res: any) => {
        const isSuccess =
          res?.succeeded ||
          res?.retorno?.codigo === 200 ||
          res?.retorno?.codigo === 201;

        if (isSuccess) {
          const authData = res?.data || res;
          if (authData?.accessToken) {
            this.handleAuthResponse(authData);
          }

          return res;
        }

        throw new Error(
          res?.retorno?.mensagem || res?.message || 'Falha ao registar',
        );
      }),

      catchError((err) => {
        return throwError(
          () => new Error(this.parseError(err, 'Erro inesperado ao registar')),
        );
      }),
    );
  }

  /** Verifica se o utilizador está autenticado */
  isAuthenticatedChange(): Observable<boolean> {
    return this.isAuthenticated.asObservable();
  }

  login(credentials: {
    userNameOrEmail: string;
    password: string;
  }): Observable<any> {
    return this.apiService.post(`/Auth/Login`, credentials).pipe(
      map((res: any) => {
        if (res?.retorno?.codigo === 200 && res?.data) {
          this.handleAuthResponse(res.data);
          return res;
        }

        this.isAuthenticated.next(false);

        throw new Error(res?.retorno?.mensagem || 'Falha no login');
      }),

      catchError((err) => {
        this.isAuthenticated.next(false);

        let errorMsg = 'Erro inesperado ao efetuar login';

        try {
          // Caso venha como objeto normal
          if (err?.error?.retorno?.mensagem) {
            errorMsg = err.error.retorno.mensagem;
          } else if (err?.error?.message) {
            errorMsg = err.error.message;
          }
          // Caso venha string JSON
          else if (typeof err?.message === 'string') {
            const cleaned = err.message.replace('Error: ', '');
            const parsed = JSON.parse(cleaned);

            errorMsg =
              parsed?.error?.retorno?.mensagem ||
              parsed?.error?.message ||
              parsed?.message ||
              errorMsg;
          }
        } catch (e) {
          errorMsg = err?.message || errorMsg;
        }

        return throwError(() => new Error(errorMsg));
      }),
    );
  }

  private parseJwt(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      if (!base64Url) return null;
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      );
      return JSON.parse(jsonPayload);
    } catch {
      return null;
    }
  }

  private handleAuthResponse(res: any) {
    if (res.accessToken) {
      localStorage.setItem(this.accessTokenKey, res.accessToken);
    }
    if (res.refreshToken) {
      localStorage.setItem(this.refreshTokenKey, res.refreshToken);
    }
    if (res.expiresAt) {
      localStorage.setItem(this.expiresAtKey, res.expiresAt);
    }

    const claimsObj: Record<string, string> = {};

    // 1. Decode claims from accessToken if available
    if (res.accessToken) {
      const jwtClaims = this.parseJwt(res.accessToken);
      if (jwtClaims && typeof jwtClaims === 'object') {
        Object.keys(jwtClaims).forEach((k) => {
          claimsObj[k] = jwtClaims[k];
        });
      }
    }

    // 2. Merge claims from response if available
    if (res.claims) {
      if (Array.isArray(res.claims)) {
        res.claims.forEach((c: any) => {
          const key = c.key || c.type || c.claimType;
          if (key) claimsObj[key] = c.value;
        });
      } else if (typeof res.claims === 'object') {
        Object.assign(claimsObj, res.claims);
      }
    }

    // 3. Include user object fields if available
    const user = res.user || res.utilizador || res;
    if (user && typeof user === 'object') {
      if (user.id && !claimsObj['id']) claimsObj['id'] = user.id;
      if (user.userName && !claimsObj['username'])
        claimsObj['username'] = user.userName;
      if (user.email && !claimsObj['email']) claimsObj['email'] = user.email;
    }

    localStorage.setItem(this.claimsKey, JSON.stringify(claimsObj));
    this.isAuthenticated.next(true);
  }

  logout(): void {
    const tokens = {
      accessToken: this.getAccessToken(),
      refreshToken: this.getRefreshToken(),
    };

    this.apiService.post(`/Auth/Logout`, tokens).subscribe({
      next: () => {
        this.clearSession();
      },
      error: () => {
        // Mesmo se o logout falhar no backend, limpar a sessão localmente.
        this.clearSession();
      },
    });
  }

  clearSession() {
    localStorage.removeItem(this.accessTokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.expiresAtKey);
    localStorage.removeItem(this.claimsKey);
    this.isAuthenticated.next(false);
    setTimeout(() => {
      this.router.navigate(['/']);
    }, 300);
  }

  getAccessToken(): string | null {
    return localStorage.getItem(this.accessTokenKey);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenKey);
  }

  getClaims(): any {
    const claims = localStorage.getItem(this.claimsKey);
    return claims ? JSON.parse(claims) : null;
  }

  isLoggedIn(): boolean {
    const isAuth = this.checkInitialAuthState();
    if (this.isAuthenticated.value !== isAuth) {
      this.isAuthenticated.next(isAuth);
    }
    return isAuth;
  }

  refresh(): Observable<any> {
    return this.apiService
      .post(`/Auth/Refresh`, {
        refreshToken: this.getRefreshToken(),
        accessToken: this.getAccessToken(),
      })
      .pipe(
        map((res: any) => {
          if (res?.retorno?.codigo === 200 && res?.data) {
            this.handleAuthResponse(res.data);
            return res.data;
          } else {
            throw {
              message: res?.retorno?.mensagem || 'Falha ao atualizar token',
            };
          }
        }),
        catchError((err) => {
          return throwError(
            () =>
              new Error(
                this.parseError(err, 'Erro inesperado ao atualizar token'),
              ),
          );
        }),
      );
  }

  // Enviar email de recuperação de senha
  forgotPassword(email: string): Observable<any> {
    return this.apiService.post(`/Auth/ForgotPassword`, { email }).pipe(
      map((res: any) => {
        if (res?.retorno?.codigo === 200) {
          return res.data;
        } else {
          throw {
            message: res?.retorno?.mensagem || 'Falha ao recuperar senha',
          };
        }
      }),
      catchError((err) => {
        return throwError(
          () =>
            new Error(
              this.parseError(err, 'Erro inesperado ao recuperar senha'),
            ),
        );
      }),
    );
  }

  //validar codigo de confirmarcao
  verifyResetCode(data: { email: string; codigo: string }): Observable<any> {
    return this.apiService.post(`/Auth/VerifyResetCode`, data).pipe(
      map((res: any) => {
        if (res?.retorno?.codigo === 200) {
          return res.data;
        } else {
          throw {
            message: res?.retorno?.mensagem || 'Falha ao verificar  codigo',
          };
        }
      }),
      catchError((err) => {
        return throwError(
          () =>
            new Error(
              this.parseError(err, 'Erro inesperado ao verificar codigo'),
            ),
        );
      }),
    );
  }

  // redefinir senha
  resetPassword(data: {
    email: string;
    codigo: string;
    novaSenha: string;
  }): Observable<any> {
    return this.apiService.post(`/Auth/ResetPassword`, data).pipe(
      map((res: any) => {
        if (res?.retorno?.codigo === 200) {
          return res.data;
        } else {
          throw {
            message: res?.retorno?.mensagem || 'Falha ao redefinir senha',
          };
        }
      }),
      catchError((err) => {
        return throwError(
          () =>
            new Error(
              this.parseError(err, 'Erro inesperado ao redefinir senha'),
            ),
        );
      }),
    );
  }

  // Ativacao de conta
  activateAccount(userId: string, token: string): Observable<any> {
    const params = new HttpParams().set('userId', userId).set('token', token);

    return this.apiService.get(`/Auth/Activate`, params).pipe(
      map((res: any) => {
        if (res?.retorno?.codigo === 200) {
          return res.data;
        } else if (res?.retorno?.codigo === 400) {
          throw { message: res?.retorno?.mensagem || 'Falha na operação' };
        } else {
          throw { message: res?.retorno?.mensagem || 'Falha ao ativar conta' };
        }
      }),
      catchError((err) => {
        return throwError(
          () =>
            new Error(this.parseError(err, 'Erro inesperado ao ativar conta')),
        );
      }),
    );
  }

  private parseError(err: any, defaultMsg: string): string {
    let errorMsg = defaultMsg;
    try {
      if (err?.error?.retorno?.mensagem) {
        errorMsg = err.error.retorno.mensagem;
      } else if (err?.error?.message) {
        errorMsg = err.error.message;
      } else if (typeof err?.message === 'string') {
        const cleaned = err.message.replace('Error: ', '');
        const parsed = JSON.parse(cleaned);

        errorMsg =
          parsed?.error?.retorno?.mensagem ||
          parsed?.error?.message ||
          parsed?.message ||
          errorMsg;
      }
    } catch (e) {
      errorMsg = err?.message || errorMsg;
    }
    return errorMsg;
  }
}
